import Head from 'next/head'
import { type BoxProps, Box, Stack, GlobalStyles } from '@mui/material'
import { useEffect, forwardRef } from 'react'
import { mergeSx } from 'lib/helpers/mergeSx/mergeSx'
import { ThemeColorCommonBackground } from 'settle-ui/tokens'
import type { HeaderProps } from 'routes/components/ExternalAppLayout/components/Header/Header'
import { useBusinessIdPathParam } from 'lib/navigation/hooks/useBusinessIdPathParam/useBusinessIdPathParam'
import { setStoredBusinessId } from 'lib/helpers/businessIdStorage/businessIdStorage'
import { useUrlBasedSnackbar } from 'routes/hooks/useUrlBasedSnackbar/useUrlBasedSnackbar'
import * as $ from './ExternalAppLayout.styled'

export const ROOT_ID = 'external-app-layout'
const GLOBAL_STYLES = (
  <GlobalStyles
    styles={{
      'html, body': {
        position: 'fixed',
        overflow: 'hidden',
        background: ThemeColorCommonBackground,
      },
      [`html, body, #__next, #${ROOT_ID}`]: {
        height: '100%',
        width: '100%',
      },
    }}
  />
)

export interface ExternalAppLayoutProps extends Pick<HeaderProps, 'loggedIn' | 'links'>, Pick<BoxProps, 'sx'> {
  slotProps?: { header?: Pick<React.ComponentProps<typeof $.Header>, 'sx'> }
  'data-testid'?: string
}

export const ExternalAppLayout = forwardRef<HTMLDivElement, React.PropsWithChildren<ExternalAppLayoutProps>>(
  ({ links, children, loggedIn, sx, slotProps, ...props }, ref) => {
    useUrlBasedSnackbar()

    const businessId = useBusinessIdPathParam()

    useEffect(() => {
      if (businessId) {
        setStoredBusinessId(businessId)
      }
    }, [businessId])

    return (
      <>
        {GLOBAL_STYLES}
        <Head>
          <meta name="viewport" content="minimum-scale=1,initial-scale=1,width=device-width,shrink-to-fit=no" />
        </Head>
        <Box
          {...props}
          ref={ref}
          id={ROOT_ID}
          sx={mergeSx(
            {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowY: 'auto',
            },
            sx,
          )}
        >
          <$.Header loggedIn={loggedIn} links={links} {...slotProps?.header} />
          <Stack sx={{ flex: 1 }}>{children}</Stack>
          <$.Footer />
        </Box>
      </>
    )
  },
)

ExternalAppLayout.displayName = 'ExternalAppLayout'
